import React__default, { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Button } from '../atoms/Button.js';
import { Dropdown } from './Dropdown.js';
import './Pagination.scss';

const PAGINATION_BUTTON_WIDTH = 32;
const PAGINATION_BUTTON_GAP = 8;
const Pagination = ({ variant = 'default', forceLayout, currentPage, onNavigateTo, totalItems, navigationOnly = false, onItemsPerPageChange, itemsPerPageOptions = [10, 25, 50, 100], itemsPerPage = itemsPerPageOptions[0], className, 'aria-label': ariaLabel = 'Pagination', hideFirstLastButtons = false, }) => {
    // Compute pageCount internally based on totalItems and itemsPerPage
    const pageCount = Math.ceil(totalItems / itemsPerPage);
    // Ensure currentPage is 1-based and within valid range
    const normalizedCurrentPage = Math.max(1, Math.min(currentPage, pageCount));
    // All hooks must be called before any early returns
    const controlsRef = useRef(null);
    const navRef = useRef(null);
    const [availableWidth, setAvailableWidth] = useState(0);
    const [defaultLayout, setDefaultLayout] = useState('inline');
    // Set up a resize observer for the nav element to handle layout changes
    useLayoutEffect(() => {
        const layoutObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                // Set layout based on width: inline if > 400px, stacked if ≤ 400px
                setDefaultLayout(width > 600 ? 'inline' : 'stacked');
            }
        });
        if (navRef.current) {
            layoutObserver.observe(navRef.current);
        }
        return () => layoutObserver.disconnect();
    }, []);
    // Set up a separate resize observer for the controls to update page numbers
    useLayoutEffect(() => {
        const controlsObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setAvailableWidth(width);
            }
        });
        if (navigationOnly && navRef.current) {
            controlsObserver.observe(navRef.current);
        }
        else if (controlsRef.current) {
            controlsObserver.observe(controlsRef.current);
        }
        return () => controlsObserver.disconnect();
    }, []);
    // Generate page numbers to display
    const getPageNumbers = (availWidth) => {
        const pages = [];
        const buttonWidth = PAGINATION_BUTTON_WIDTH;
        const gap = PAGINATION_BUTTON_GAP;
        // Reserve space for navigation buttons and gaps
        // Always reserve space for prev/next buttons (2 buttons)
        let reservedSpace = (buttonWidth + gap) * 2;
        // Add space for first/last buttons if not hidden (2 more buttons)
        if (!hideFirstLastButtons) {
            reservedSpace += (buttonWidth + gap) * 2;
        }
        const availableSpaceForPages = availWidth - reservedSpace;
        // Calculate how many page buttons can fit
        const maxPageButtons = Math.floor(availableSpaceForPages / (buttonWidth + gap));
        // If we can show all pages, do so
        if (pageCount <= maxPageButtons) {
            for (let i = 1; i <= pageCount; i++) {
                pages.push(i);
            }
            return pages;
        }
        // Show a window of pages centered around the current page
        const halfWindow = Math.floor(maxPageButtons / 2);
        let startPage = Math.max(1, normalizedCurrentPage - halfWindow);
        const endPage = Math.min(pageCount, startPage + maxPageButtons - 1);
        // Adjust if we're near the end
        if (endPage === pageCount) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }
        // Generate the page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };
    // Memoize page numbers calculation
    const pageNumbers = useMemo(() => {
        return getPageNumbers(availableWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: getPageNumbers is stable, deps are the values it uses
    }, [pageCount, normalizedCurrentPage, availableWidth]);
    // Don't render if there are no items
    if (totalItems === 0) {
        return null;
    }
    const isPageActivatable = (page) => {
        // If there's only one page (itemsPerPage >= totalItems), no page should be activatable
        if (pageCount === 1) {
            return false;
        }
        return page >= 1 && page <= pageCount && page !== normalizedCurrentPage;
    };
    const handleItemsPerPageChange = (value) => {
        const newItemsPerPage = parseInt(value, 10);
        onItemsPerPageChange(newItemsPerPage);
    };
    // Calculate the dynamic item count range
    const getItemCountRange = () => {
        const startItem = (normalizedCurrentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(normalizedCurrentPage * itemsPerPage, totalItems);
        return `${startItem} - ${endItem}`;
    };
    // Determine the final layout: use forceLayout if provided, otherwise use dynamic layout
    // If navigationOnly is true, force inline layout since there's no content to stack
    const layoutMode = navigationOnly ? 'inline' : (forceLayout ?? defaultLayout);
    const handlePageClick = (pageNumber) => {
        if (isPageActivatable(pageNumber)) {
            onNavigateTo(pageNumber);
        }
    };
    return (React__default.createElement("nav", { ref: navRef, "data-rev-component": 'pagination', "data-rev-variant": variant, "data-rev-layout": layoutMode, "data-rev-navigation-only": navigationOnly, className: className, "aria-label": ariaLabel, style: {
            '--buttonWidth': `${PAGINATION_BUTTON_WIDTH}px`,
            '--buttonGap': `${PAGINATION_BUTTON_GAP}px`,
        } },
        !navigationOnly && (React__default.createElement("div", { className: 'pagination-list-info--rev' },
            getItemCountRange(),
            " of ",
            totalItems,
            " items")),
        React__default.createElement("div", { className: 'pagination-pages--rev', ref: controlsRef },
            !hideFirstLastButtons && (React__default.createElement(Button, { variant: 'ghost', size: 'icon', className: 'pagination-button-first--rev', onPress: () => handlePageClick(1), isDisabled: normalizedCurrentPage <= 1 || pageCount === 1, ariaLabel: 'Go to first page' },
                React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', "aria-hidden": 'true' },
                    React__default.createElement("path", { d: 'M10.9308 5.51174C11.2004 5.19743 11.674 5.16122 11.9884 5.43069C12.3026 5.70027 12.3389 6.17389 12.0695 6.4883L7.34485 12L12.0695 17.5117C12.3388 17.8262 12.3027 18.2998 11.9884 18.5694C11.674 18.8388 11.2004 18.8025 10.9308 18.4883L6.03235 12.7705C5.93739 12.6599 5.86592 12.535 5.81946 12.4043C5.77301 12.2736 5.75013 12.1363 5.75012 12C5.75012 11.8637 5.77301 11.7265 5.81946 11.5957L5.85852 11.499C5.90239 11.4033 5.96006 11.3116 6.03137 11.2285H6.03235L10.9308 5.51174ZM16.9308 5.51174C17.2004 5.19743 17.674 5.16122 17.9884 5.43069C18.3026 5.70027 18.3389 6.17389 18.0695 6.4883L13.3448 12L18.0695 17.5117C18.3388 17.8262 18.3027 18.2998 17.9884 18.5694C17.674 18.8388 17.2004 18.8025 16.9308 18.4883L12.0323 12.7705C11.9374 12.6599 11.8659 12.535 11.8195 12.4043C11.773 12.2736 11.7501 12.1363 11.7501 12C11.7501 11.8637 11.773 11.7265 11.8195 11.5957L11.8585 11.499C11.9024 11.4033 11.9601 11.3116 12.0314 11.2285H12.0323L16.9308 5.51174Z', fill: 'currentColor' })))),
            React__default.createElement(Button, { variant: 'ghost', size: 'icon', className: 'pagination-button-prev--rev', onPress: () => handlePageClick(normalizedCurrentPage - 1), isDisabled: normalizedCurrentPage <= 1 || pageCount === 1, ariaLabel: 'Go to previous page' },
                React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', "aria-hidden": 'true' },
                    React__default.createElement("path", { d: 'M13.4777 5.46193C13.7746 5.17356 14.2496 5.18073 14.5382 5.47756C14.8264 5.77455 14.8194 6.24952 14.5226 6.5381L8.9054 12L14.5226 17.4619C14.8192 17.7506 14.8265 18.2255 14.5382 18.5225C14.2496 18.8192 13.7746 18.8263 13.4777 18.5381L7.59778 12.8203C7.48917 12.7148 7.40198 12.588 7.34192 12.4473C7.28178 12.3062 7.25015 12.154 7.25012 12C7.25012 11.846 7.28179 11.6938 7.34192 11.5528C7.40198 11.412 7.48913 11.2853 7.59778 11.1797L13.4777 5.46193Z', fill: 'currentColor' }))),
            pageNumbers.map((pageNumber) => {
                const isActive = pageNumber === normalizedCurrentPage;
                return (React__default.createElement(Button, { key: pageNumber, variant: isActive ? 'filled' : 'ghost', size: 'small', className: 'pagination-button-page--rev', onPress: () => handlePageClick(pageNumber), ariaLabel: `Go to page ${pageNumber}`, ariaCurrent: isActive ? 'page' : undefined }, pageNumber));
            }),
            React__default.createElement(Button, { variant: 'ghost', size: 'icon', className: 'pagination-button-next--rev', onPress: () => onNavigateTo(normalizedCurrentPage + 1), isDisabled: normalizedCurrentPage >= pageCount || pageCount === 1, ariaLabel: 'Go to next page' },
                React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', "aria-hidden": 'true' },
                    React__default.createElement("path", { d: 'M9.46209 5.47754C9.75073 5.18075 10.2257 5.17355 10.5226 5.46191L16.4025 11.1797C16.5111 11.2853 16.5984 11.4121 16.6584 11.5527C16.7185 11.6938 16.7502 11.8461 16.7502 12C16.7501 12.154 16.7185 12.3062 16.6584 12.4473C16.5983 12.588 16.5111 12.7147 16.4025 12.8203L10.5226 18.5381C10.2257 18.8264 9.75075 18.819 9.46209 18.5225C9.17371 18.2255 9.18089 17.7506 9.47771 17.4619L15.0939 12L9.47771 6.53808C9.18089 6.24944 9.17371 5.77452 9.46209 5.47754Z', fill: 'currentColor' }))),
            !hideFirstLastButtons && (React__default.createElement(Button, { variant: 'ghost', size: 'icon', className: 'pagination-button-last--rev', onPress: () => handlePageClick(pageCount), isDisabled: normalizedCurrentPage >= pageCount || pageCount === 1, ariaLabel: 'Go to last page' },
                React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', "aria-hidden": 'true' },
                    React__default.createElement("path", { d: 'M6.01178 5.43066C6.32623 5.16124 6.79984 5.19742 7.06939 5.51172L11.9678 11.2285H11.9688C12.0401 11.3116 12.0978 11.4034 12.1417 11.499L12.1807 11.5957C12.2271 11.7264 12.2501 11.8637 12.2501 12C12.25 12.1363 12.2272 12.2736 12.1807 12.4043C12.1343 12.535 12.0628 12.6598 11.9678 12.7705L7.06939 18.4883C6.79987 18.8026 6.32623 18.8387 6.01178 18.5693C5.69746 18.2998 5.66125 17.8262 5.93072 17.5117L10.6553 12L5.93072 6.48828C5.66125 6.17383 5.69746 5.70023 6.01178 5.43066ZM12.0118 5.43066C12.3262 5.16124 12.7998 5.19742 13.0694 5.51172L17.9678 11.2285H17.9688C18.0401 11.3116 18.0978 11.4034 18.1417 11.499L18.1807 11.5957C18.2271 11.7264 18.2501 11.8637 18.2501 12C18.25 12.1363 18.2272 12.2736 18.1807 12.4043C18.1343 12.535 18.0628 12.6598 17.9678 12.7705L13.0694 18.4883C12.7999 18.8026 12.3262 18.8387 12.0118 18.5693C11.6975 18.2998 11.6612 17.8262 11.9307 17.5117L16.6553 12L11.9307 6.48828C11.6612 6.17383 11.6975 5.70023 12.0118 5.43066Z', fill: 'currentColor' }))))),
        !navigationOnly && (React__default.createElement("div", { className: 'pagination-items-per-page-container--rev' },
            React__default.createElement("label", { htmlFor: 'items-per-page' }, "Items per page:"),
            React__default.createElement(Dropdown, { options: itemsPerPageOptions.map((option) => ({
                    id: option.toString(),
                    label: option.toString(),
                })), value: itemsPerPage.toString(), onSelectionChange: handleItemsPerPageChange, className: 'pagination-select--rev', buttonVariant: 'outline', size: 'tiny' })))));
};

export { Pagination };
//# sourceMappingURL=Pagination.js.map
